import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, GraduationCapIcon, BriefcaseIcon, CarIcon, HeartPulseIcon, TrendingUpIcon, HomeIcon, StethoscopeIcon, SmartphoneIcon, PlaneIcon, ArrowRightIcon, StarIcon, ShieldCheckIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface ProductCatalogueProps {
  navigate: (screen: string) => void;
}
export function ProductCatalogue({
  navigate
}: ProductCatalogueProps) {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Savings', 'Protection', 'Health', 'Assets'];
  const products = [{
    id: 'goalguard',
    title: 'GoalGuard',
    desc: 'Target savings with guaranteed protection.',
    price: '₦5,000',
    icon: ShieldCheckIcon,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    category: 'Savings'
  }, {
    id: 'child-education',
    title: 'Future Scholar Fund',
    desc: 'Save for their future with life cover.',
    price: '₦15,500',
    icon: GraduationCapIcon,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    category: 'Savings'
  }, {
    id: 'job-loss',
    title: 'Income Shield',
    desc: 'Income protection if you lose your job.',
    price: '₦2,500',
    icon: BriefcaseIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    category: 'Protection'
  }, {
    id: 'motor',
    title: 'AutoGuard',
    desc: 'Comprehensive cover + savings.',
    price: '₦15,000',
    icon: CarIcon,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    category: 'Assets'
  }, {
    id: 'life',
    title: 'Legacy Plan',
    desc: 'Pure protection + long-term savings.',
    price: '₦3,000',
    icon: HeartPulseIcon,
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    category: 'Protection'
  }, {
    id: 'annuity',
    title: 'Freedom Fund',
    desc: 'Guaranteed income for life.',
    price: '₦10,000',
    icon: TrendingUpIcon,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    category: 'Savings'
  }, {
    id: 'home',
    title: 'Property Shield',
    desc: 'Protect property & save for upgrades.',
    price: '₦4,000',
    icon: HomeIcon,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    category: 'Assets'
  }, {
    id: 'health',
    title: 'Wellness Plan',
    desc: 'HMO and indemnity plans.',
    price: '₦8,000',
    icon: StethoscopeIcon,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    category: 'Health'
  }, {
    id: 'travel',
    title: 'TravelGuard',
    desc: 'Comprehensive travel protection.',
    price: '₦5,000',
    icon: PlaneIcon,
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    category: 'Protection'
  }];
  const filteredProducts = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 10
    },
    show: {
      opacity: 1,
      scale: 1,
      y: 0
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-2603">
      <TopBar title="Products" onNotificationClick={() => navigate('notifications')} onProfileClick={() => navigate('profile')} data-id="element-2604" />

      <div className="px-5 pt-4 pb-6 md:max-w-5xl md:mx-auto" data-id="element-2605">
        {/* Welcome Banner */}
        <div className="bg-[#2BA89D]/10 rounded-[2rem] p-5 mb-6 flex items-center justify-between border border-[#2BA89D]/20 shadow-sm" data-id="element-2606">
          <div data-id="element-2607">
            <h2 className="text-lg font-black text-[#0D3B3B] tracking-tight" data-id="element-2608">
              Find your perfect plan
            </h2>
            <p className="text-xs text-[#0D3B3B]/70 mt-1 font-medium" data-id="element-2609">
              Explore our curated protection plans.
            </p>
          </div>
          <motion.img src="https://cdn.magicpatterns.com/uploads/fCtTe1ZG3m1ZsjnpHbbpZx/DD_standing_front_1.png" alt="Mascot" className="w-16 h-16 object-contain origin-bottom drop-shadow-md" animate={{
          rotate: [-5, 5, -5]
        }} transition={{
          repeat: Infinity,
          duration: 3,
          ease: 'easeInOut'
        }} data-id="element-2610" />
        </div>

        {/* Search */}
        <div className="relative mb-6" data-id="element-2611">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2612" />
          <input type="text" placeholder="Search products, underwriters..." className="w-full bg-white border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] shadow-sm" data-id="element-2613" />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8 pb-1" data-id="element-2614">
          {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${activeCategory === cat ? 'bg-[#0D3B3B] text-[#FFD700] shadow-md' : 'bg-white text-gray-500 border border-[#0D3B3B]/10 hover:bg-gray-50'}`} data-id="element-2615">
              {cat}
            </button>)}
        </div>

        {/* Featured Banner */}
        <div className="bg-[#FFD700] rounded-[2rem] p-6 text-[#0D3B3B] mb-8 shadow-xl shadow-[#0D3B3B]/10 border border-[#FFD700]/50 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform" data-id="element-2616">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/40 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-2617"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#2BA89D]/20 rounded-full blur-2xl -ml-10 -mb-10" data-id="element-2618"></div>

          <div className="relative z-10" data-id="element-2619">
            <div className="inline-flex items-center gap-1 bg-white/40 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3" data-id="element-2620">
              <StarIcon className="w-3 h-3 fill-[#0D3B3B]" data-id="element-2621" /> Featured
            </div>
            <h2 className="text-2xl font-black mb-1 tracking-tight" data-id="element-2622">
              GoalGuard
            </h2>
            <p className="text-sm font-medium opacity-90 mb-5" data-id="element-2623">
              Save smarter with guaranteed protection.
            </p>
            <button onClick={() => navigate('product-detail', 'goalguard')} className="bg-[#0D3B3B] text-[#FFD700] text-xs font-bold px-5 py-2.5 rounded-xl flex items-center gap-2 hover:bg-[#092D2D] transition-colors shadow-md" data-id="element-2624">
              Explore <ArrowRightIcon className="w-4 h-4" data-id="element-2625" />
            </button>
          </div>
        </div>

        {/* Product Grid */}
        <div className="mb-4" data-id="element-2626">
          <h2 className="text-lg font-black text-[#0D3B3B] tracking-tight mb-4 px-1" data-id="element-2627">
            {activeCategory === 'All' ? 'All Products' : activeCategory}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-2628">
            {products.filter(p => activeCategory === 'All' || p.category === activeCategory).map((product, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.05
          }} onClick={() => navigate('product-detail', product.id)} className="bg-white rounded-[1.5rem] p-5 border border-[#0D3B3B]/5 shadow-sm hover:shadow-md hover:border-[#2BA89D]/20 transition-all cursor-pointer group" data-id="element-2629">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${product.bg} ${product.color} group-hover:scale-110 transition-transform duration-300`} data-id="element-2630">
                    <product.icon className="w-6 h-6" data-id="element-2631" />
                  </div>
                  <h3 className="font-bold text-[#0D3B3B] text-sm mb-1 line-clamp-1 group-hover:text-[#2BA89D] transition-colors" data-id="element-2632">
                    {product.title}
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-3 line-clamp-2 leading-relaxed font-medium" data-id="element-2633">
                    {product.desc}
                  </p>
                  <div className="mt-auto pt-3 border-t border-gray-50 flex items-center justify-between" data-id="element-2634">
                    <span className="text-xs font-black text-[#0D3B3B]" data-id="element-2635">
                      {product.price}
                    </span>
                    <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#0D3B3B] group-hover:text-[#FFD700] transition-colors" data-id="element-2636">
                      <ArrowRightIcon className="w-3 h-3" data-id="element-2637" />
                    </div>
                  </div>
                </motion.div>)}
          </div>
        </div>
      </div>
    </motion.div>;
}